import type { getDb } from '@/db';
import type { StoredCheckoutAnalyticsContext } from '@/payment/attribution/checkout-analytics-context';
import type { PaymentProviderId } from '@/payment/provider-id';
import type { Price } from '@/payment/types';

type CheckoutDbClient = Awaited<ReturnType<typeof getDb>>;

export type CheckoutDb =
  | CheckoutDbClient
  | Parameters<Parameters<CheckoutDbClient['transaction']>[0]>[0];

export type CheckoutCustomer = {
  id: string;
  name: string;
  email: string;
};

export type CheckoutStartResult = {
  checkoutId: string;
  checkoutUrl: string;
};

export type CheckoutResumeTarget = {
  provider: PaymentProviderId;
  priceId: string;
};

export type CheckoutMetadata = Record<string, string> | undefined;

export const checkoutErrorCodes = [
  'missing_required',
  'plan_unavailable',
  'price_unavailable',
  'package_unavailable',
  'invalid_package_price',
  'provider_unavailable',
  'provider_not_enabled',
  'provider_not_supported',
  'subscription_blocked',
  'pending_checkout_blocked',
  'pending_checkout_recovery_failed',
  'checkout_timed_out',
  'checkout_create_failed',
] as const;

export type CheckoutErrorCode = (typeof checkoutErrorCodes)[number];

export type CheckoutFailure = {
  success: false;
  code: CheckoutErrorCode;
  error: string;
};

export type CheckoutPreparationResult<T> =
  | {
      success: true;
      params: T;
    }
  | CheckoutFailure;

export type SubscriptionCheckoutServiceParams = {
  analyticsContext?: StoredCheckoutAnalyticsContext;
  db: CheckoutDb;
  currentUser: CheckoutCustomer;
  provider: PaymentProviderId;
  locale: string;
  safeCallback: string;
  metadata?: CheckoutMetadata;
  planId: string;
  planPriceIds: string[];
  priceId: string;
  price: Pick<Price, 'interval' | 'amount' | 'currency'>;
  providerPriceId: string;
  pendingRunId?: string;
};

export type CreditPackageCheckoutTarget = {
  id: string;
  amount: number;
  price: {
    priceId: string;
    amount: number;
    currency: string;
  };
};

export type CreditCheckoutServiceParams = {
  analyticsContext?: StoredCheckoutAnalyticsContext;
  db: CheckoutDb;
  currentUser: CheckoutCustomer;
  provider: PaymentProviderId;
  locale: string;
  safeCallback: string;
  metadata?: CheckoutMetadata;
  packageId: string;
  priceId: string;
  creditPackage: CreditPackageCheckoutTarget;
  providerPriceId: string;
  pendingRunId?: string;
};
